import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { profileApi } from '@/lib/api'

type Theme = 'light' | 'dark'

interface ThemeStore {
  theme: Theme
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
  /** Set theme from server without persisting back (avoids loop on login) */
  setThemeFromServer: (theme: Theme) => void
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

function persistToServer(theme: Theme) {
  profileApi.update({ themePreference: theme }).catch(() => {
    // Best-effort — don't block the UI if this fails
  })
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      theme: 'light' as Theme,

      toggleTheme: () => set((state) => {
        const newTheme = state.theme === 'dark' ? 'light' : 'dark'
        applyTheme(newTheme)
        persistToServer(newTheme)
        return { theme: newTheme }
      }),

      setTheme: (theme: Theme) => {
        applyTheme(theme)
        persistToServer(theme)
        set({ theme })
      },

      setThemeFromServer: (theme: Theme) => {
        applyTheme(theme)
        set({ theme })
      },
    }),
    {
      name: 'relay-theme',
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme)
      },
    }
  )
)
